import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BlogService, ReactionType } from '../../shared/services/blog.service';
import { CommentService, IComment, IReply } from '../../shared/services/comment.service';
import { UserService } from '../../shared/services/user.service';
// import { ReactionSoundService } from '../../shared/services/reaction-sound.service';
import { DropdownItemComponent } from '../../components/ui/dropdown/dropdown-item/dropdown-item';
import { DropdownComponent } from '../../components/ui/dropdown/dropdown';
import { FriendRequestService, FriendshipStatus } from '../../shared/services/friend-request.service';
import { MentionInputDirective, MentionUser } from '../../shared/directives/mention-input.directive';
import { SwipeDirective } from '../../shared/directives/swipe.directive';
import { MentionDropdownComponent } from '../../components/ui/dropdown/mention-dropdown/mention-dropdown.component';
import { RelationshipService } from '../../shared/services/relationship.service';
import { Modal } from '../../components/ui/modal/modal';
import { ConfirmationPopup } from '../../components/ui/confirmation-popup/confirmation-popup';
import { AuthStateService } from '../../shared/states/auth.state';
import { FavouriteService } from '../../shared/services/favourites.service';
import { ShareSheetComponent } from '../../components/ui/share-sheet/share-sheet';
import { ShareConfig } from '../../shared/services/share.service';
import { CollabrationRequestService } from '../../shared/services/collabration-request.service';
import { FriendRequestLimitPopup } from '../../components/ui/friend-request-limit-popup/friend-request-limit-popup';
import { SeoService } from '../../shared/services/seo.service';
import { AdminPreviewService } from '../../shared/services/admin-preview.service';
import { ReportModal } from '../../components/ui/report-modal/report-modal';
import { BlockUserConfirm } from '../../components/ui/block-user-confirm/block-user-confirm';
import { ReportService } from '../../shared/services/report.service';

export const REACTIONS: {
  type: ReactionType;
  emoji: string;
  label: string;
  color: string;
  bg: string;
}[] = [
    { type: 'LIKE', emoji: '👍', label: 'Like', color: '#2078f4', bg: '#e7f3ff' },
    { type: 'HEART', emoji: '❤️', label: 'Love', color: '#f33e58', bg: '#ffe7eb' },
    { type: 'HUG', emoji: '🤗', label: 'Hug', color: '#f7b125', bg: '#fff4e0' },
    { type: 'WOW', emoji: '😮', label: 'Wow', color: '#f7b125', bg: '#fff4e0' },
    { type: 'SAD', emoji: '😢', label: 'Sad', color: '#7c93a3', bg: '#eef2f5' },
    { type: 'HAHA', emoji: '😂', label: 'Haha', color: '#f7b125', bg: '#fff4e0' },
  ];

export const QUICK_EMOJIS = ['😀', '❤️', '😄', '😂', '😍', '😘', '😈', '😡', '🔥', '👍', '🥺', '💔', '👏'];

export const EMOJI_CATEGORIES: { label: string; emojis: string[] }[] = [
  {
    label: 'Smileys',
    emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌',
      '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭',
      '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔'],
  },
  {
    label: 'Gestures',
    emojis: ['👍', '👎', '👊', '✊', '🤛', '🤜', '🤞', '✌️', '🤟', '🤘', '👌', '🤌', '🤏', '👈',
      '👉', '👆', '👇', '☝️', '👋', '🤚', '🖐', '✋', '🖖', '💪', '🦾', '🤲', '👐', '🙌', '👏'],
  },
  {
    label: 'Hearts',
    emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓',
      '💗', '💖', '💘', '💝', '💟'],
  },
  {
    label: 'Activities',
    emojis: ['🎉', '🎊', '🎈', '🎁', '🎂', '🍰', '🥂', '🍾', '🎆', '🎇', '✨', '🌟', '⭐', '🌠',
      '🎯', '🎮', '🕹', '🎲', '🎪', '🎭', '🎨', '🖼', '🎬', '🎤', '🎧', '🎵', '🎶', '🎸'],
  },
];

interface ReactionCounts {
  LIKE: number; HEART: number; HUG: number;
  WOW: number; SAD: number; HAHA: number;
}

interface BlogBlockVM {
  type: 'TEXT' | 'IMAGE' | 'VIDEO';
  html?: string;
  url?: string;
}

interface BlogCollaborator {
  _id: string;
  displayName: string;
  userName: string;
  profileImage?: { urlSmall?: string };
  friendship: { status: string; requestId?: string | null };
  isFollowed: boolean;
  isMe: boolean;
}

interface BlogDetailsVM {
  id: string;
  hostId: string;
  userId: string;
  hostName: string;
  hostAvatar: string;
  timeAgo: string;
  title: string;
  blocks: BlogBlockVM[];
  hashtags: string[];
  likes: number;
  comments: number;
  shares: number;
  myReaction: ReactionType | null;
  reactionCounts: ReactionCounts;
  totalReactions: number;
  recentUserName: string | null;
  topTypes: ReactionType[];
  showEmojiPicker: boolean;
  hoveredReaction: ReactionType | null;
  friendshipStatus: FriendshipStatus;
  friendshipRequestId: string | null;
  isFollowed: boolean;
  isMyBlog: boolean;
  acceptedCollaborators: BlogCollaborator[];
  mentions: { _id: string; displayName: string; userName: string; profileImage?: { urlSmall?: string } }[];
  commentsDisabled: boolean;
  rawCollaborations: any[];
  isFavourite: boolean;
  isRemovedByAdmin: boolean;
}

interface RecommendationVM {
  _id: string;
  userId: string;
  hostName: string;
  hostAvatar: string;
  title: string;
  author: string;
  authorDisplayName: string;
  avatar: string;
  timeAgo: string;
  text: string;
  hasMore: boolean;
  images: string[];
  mediaBlocks: BlogBlockVM[];
  activeSlide: number;
  likes: number;
  comments: number;
  shares: number;
  myReaction: ReactionType | null;
  reactions: number;
  totalReactions: number;
  reactionCounts: ReactionCounts;
  recentUserName: string | null;
  topTypes: ReactionType[];
  showEmojiPicker: boolean;
  hoveredReaction: ReactionType | null;
  isFollowed: boolean;
  isMyBlog: boolean;
  friendshipStatus: FriendshipStatus;
  friendshipRequestId: string | null;
  acceptedCollaborators: BlogCollaborator[];
}

type SocialCardVM = {
  userId: string;
  friendshipStatus: FriendshipStatus;
  friendshipRequestId: string | null;
};

type ReactionCardVM = {
  id?: string;
  _id?: string;
  likes: number;
  myReaction: ReactionType | null;
  reactionCounts: ReactionCounts;
  totalReactions: number;
  reactions?: number;
  showEmojiPicker: boolean;
  hoveredReaction: ReactionType | null;
  topTypes: ReactionType[];
  recentUserName: string | null;
};

@Component({
  selector: 'app-blog-details',
  standalone: true,
  imports: [
    CommonModule,
    ShareSheetComponent,
    FormsModule,
    DropdownComponent,
    DropdownItemComponent,
    MentionInputDirective,
    MentionDropdownComponent,
    Modal,
    RouterModule,
    ConfirmationPopup,
    FriendRequestLimitPopup,
    SwipeDirective,
    ReportModal,
    BlockUserConfirm
  ],
  templateUrl: './blog-details.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlogDetails implements OnInit {
  blogDetails: BlogDetailsVM[] | null = null;
  isLoading = true;
  hasError = false;

  // ── Recommendations ────────────────────────────────────────────────────────
  recommendations: RecommendationVM[] = [];
  recsLoading = false;
  showCollabModal = false;
  selectedBlog: any | null = null;
  showTaggedModal = false;
  isReactionOpen = false;
  reactionData: any = null;
  reactionsLoading = false;
  activeReactionFilter: ReactionType | null = null;
  private activeReactionBlogId: string | null = null;

  readonly REACTIONS = REACTIONS;
  readonly QUICK_EMOJIS = QUICK_EMOJIS;
  readonly EMOJI_CATEGORIES = EMOJI_CATEGORIES;

  private authState = inject(AuthStateService);
  private favouriteService = inject(FavouriteService);

  isFavourite = false;

  // ── Share sheet ──────────────────────────────────────────────────────────
  shareConfig: ShareConfig | null = null;
  shareCount = 0;

  // ── Friend request cooldown popup state ────────────────────────────────────
  showFriendLimitPopup = false;
  friendLimitHours: number | null = null;

  // ── Login prompt state ─────────────────────────────────────────────────────
  showLoginPrompt = false;
  loginPromptUserName = '';
  loginPromptAction = '';

  // ── Comment state ─────────────────────────────────────────────────────────
  comments: IComment[] = [];
  commentsTotal = 0;
  commentsPage = 1;
  commentsTotalPages = 1;
  commentsLoading = false;
  commentsLoaded = false;

  commentInput = '';
  replyInputs: Record<string, string> = {};
  replyingTo: string | null = null;

  editingCommentId: string | null = null;
  editInput = '';

  submitting = false;
  openDropdownId: string | null = null;
  togglingComments = false;

  // ── Emoji & GIF ───────────────────────────────────────────────────────────
  showEmojiPicker = false;
  activeEmojiTab = 0;
  activeEmojiContext: string | null = null;
  showGifPicker = false;
  gifSearchQuery = '';
  gifResults: { id: string; url: string; preview: string }[] = [];
  gifLoading = false;
  selectedGifUrl: string | null = null;
  replyGifUrls: Record<string, string> = {};

  // ── @mention state ────────────────────────────────────────────────────────
  activeMentionContext: string | null = null;
  mentionUsers: MentionUser[] = [];
  showMentionDropdown = false;

  @ViewChild('mainDir') mainDirRef?: MentionInputDirective;
  @ViewChild('replyDir') replyDirRef?: MentionInputDirective;

  private mention$ = new Subject<string>();
  private mentionSub = this.mention$
    .pipe(debounceTime(200), distinctUntilChanged())
    .subscribe(q => this.fetchMentionUsers(q));

  blogId: string | null = null;
  private GIPHY_KEY = 'mpVQUxeAA36qhojmJ6nnsrC6lekAAFfR';

  private friendRequestService = inject(FriendRequestService);
  private relationshipService = inject(RelationshipService);
  private seoService = inject(SeoService);
  private adminPreviewService = inject(AdminPreviewService);

  /** Opened from the admin panel via a preview link (not by the author) —
   *  the removed-by-admin banner then uses admin wording. */
  get isAdminPreview(): boolean {
    return !!this.adminPreviewService.findGrant('BLOG', this.blogId);
  }
  private platformId = inject(PLATFORM_ID);

  private openCommentsOnLoad = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private blogService: BlogService,
    private commentService: CommentService,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    // private soundService: ReactionSoundService,
    private elRef: ElementRef,
    private ngZone: NgZone,
    private sanitizer: DomSanitizer,
    private location: Location,
  ) { }

  /** Blog comments are an always-visible side panel rather than a tab, so
   *  arriving here from a comment notification scrolls it into view instead
   *  of switching tabs (matches post/event details' openComments behavior). */
  private scrollToComments(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    setTimeout(() => {
      document.getElementById('blog-comments-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  /** Clicking a #hashtag chip takes the user to the "All" page, filtered to
   *  that exact hashtag across posts, blogs and events. */
  goToHashtag(tag: string, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/all'], { queryParams: { hashtag: tag } });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (!id) return;

      this.blogId = id;
      this.openCommentsOnLoad = this.route.snapshot.queryParamMap.get('openComments') === '1';
      this.blogDetails = null;
      this.isLoading = true;
      this.hasError = false;
      this.comments = [];
      this.commentsTotal = 0;
      this.commentsPage = 1;
      this.commentsTotalPages = 1;
      this.commentsLoading = false;
      this.commentsLoaded = false;
      this.commentInput = '';
      this.replyInputs = {};
      this.replyingTo = null;
      this.selectedGifUrl = null;
      this.replyGifUrls = {};
      this.showGifPicker = false;
      this.showEmojiPicker = false;

      this.closeMentionDropdown();
      this.cdr.markForCheck();

      this.getBlog(id);
      this.loadComments();
    });
  }

  // ── Safe HTML helper ──────────────────────────────────────────────────────

  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html ?? '');
  }

  // ── @mention ──────────────────────────────────────────────────────────────

  onMentionSearch(query: string, context: string): void {
    this.activeMentionContext = context;
    this.mention$.next(query);
  }

  onMentionDropdownChange(open: boolean, context: string): void {
    if (open) {
      this.activeMentionContext = context;
      this.showMentionDropdown = true;
      if (this.mentionUsers.length === 0) this.fetchMentionUsers('');
    } else if (this.activeMentionContext === context) {
      this.closeMentionDropdown();
    }
    this.cdr.markForCheck();
  }

  private fetchMentionUsers(q: string): void {
    this.userService.searchUsersForMention(q).subscribe({
      next: (res: any) => {
        this.mentionUsers = res?.data ?? [];
        this.showMentionDropdown = this.mentionUsers.length > 0;
        this.cdr.markForCheck();
      },
      error: () => { this.mentionUsers = []; this.cdr.markForCheck(); },
    });
  }

  onMentionPicked(user: MentionUser, context: string, directive: MentionInputDirective): void {
    if (context === 'main') {
      this.commentInput = directive.insertMention(user, this.commentInput);
    } else {
      this.replyInputs[context] = directive.insertMention(user, this.replyInputs[context] ?? '');
    }
    this.closeMentionDropdown();
    this.cdr.markForCheck();
  }

  closeMentionDropdown(): void {
    this.showMentionDropdown = false;
    this.activeMentionContext = null;
    this.mentionUsers = [];
  }

  // ── Data ──────────────────────────────────────────────────────────────────

  getBlog(id: string): void {
    this.isLoading = true;
    this.hasError = false;
    this.blogService.getBlog(id).subscribe({
      next: (res: any) => {
        this.blogDetails = this.mapBlogs([res.data]);
        this.isFavourite = (res.data as any)?.isFavourite ?? false;
        this.isLoading = false;

        const blog = this.blogDetails[0];
        if (blog) {
          const firstImage = blog.blocks.find(b => b.type === 'IMAGE')?.url;
          const firstText = blog.blocks.find(b => b.type === 'TEXT')?.html ?? '';
          this.seoService.setSocialTags({
            title: blog.title || `${blog.hostName}'s blog`,
            description: firstText || `Blog by ${blog.hostName} on Safeword`,
            image: firstImage,
            path: `/blog/${id}`,
            type: 'article',
          });
        }

        this.fetchRecommendations(id);
        this.cdr.markForCheck();

        if (this.openCommentsOnLoad) {
          this.openCommentsOnLoad = false;
          this.scrollToComments();
        }
      },
      error: (err) => {
        console.error(err);
        this.isLoading = false;
        this.hasError = true;
        this.cdr.markForCheck();
      },
    });
  }

  // ── Recommendations ────────────────────────────────────────────────────────

  private fetchRecommendations(blogId: string): void {
    this.recsLoading = true;
    this.cdr.markForCheck();
    this.blogService.getRecommendations(blogId).subscribe({
      next: (res: any) => {
        const items: any[] = res?.data ?? [];
        this.recommendations = items.slice(0, 3).map((b: any): RecommendationVM => {
          const blocks = (b.blogInfo?.blocks ?? [])
            .slice()
            .sort((a: any, x: any) => a.position - x.position);

          const mediaBlocks: BlogBlockVM[] = blocks
            .filter((bl: any) => bl.type === 'IMAGE' || bl.type === 'VIDEO')
            .map((bl: any) => ({
              type: bl.type,
              url: bl.mediaId?.urlLarge
                ?? bl.mediaId?.urlMedium
                ?? bl.mediaId?.urlSmall
                ?? bl.mediaId?.url
                ?? bl.urlLarge
                ?? bl.urlMedium
                ?? bl.urlSmall
                ?? bl.url
                ?? '',
            }))
            .filter((bl: BlogBlockVM) => !!bl.url);

          const images = mediaBlocks
            .map((bl: BlogBlockVM) => bl.url ?? '')
            .filter(Boolean);

          const firstText = blocks.find((bl: any) => bl.type === 'TEXT');
          const rawText = firstText?.text
            ? firstText.text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
            : '';
          const raw = b?.reactions?.myReaction;
          const rawType: string | null =
            raw && typeof raw === 'object' ? (raw.type ?? null) : (raw ?? null);
          const myReaction: ReactionType | null =
            rawType && REACTIONS.some(r => r.type === rawType)
              ? (rawType as ReactionType)
              : null;
          const reactionCounts = b?.reactions?.counts ?? {
            LIKE: 0, HEART: 0, HUG: 0, WOW: 0, SAD: 0, HAHA: 0,
          };
          const totalReactions = b?.reactions?.total ?? b?.numberOfLikes ?? 0;

          const recCollaborators: BlogCollaborator[] = (b.blogInfo?.collaborations ?? [])
            .filter((c: any) => c.status === 'ACCEPTED')
            .map((c: any) => ({
              _id: c.user?._id ?? '',
              displayName: c.user?.displayName ?? '',
              userName: c.user?.userName ?? '',
              profileImage: c.user?.profileImage ?? undefined,
              friendship: c.friendship ?? c.user?.friendship ?? { status: 'NONE', requestId: null },
              isFollowed: c.isFollowed ?? c.user?.isFollowed ?? false,
              isMe: c.isMe ?? false,
            }));

          return {
            _id: b._id,
            userId: b.user?._id ?? '',
            hostName: b.user?.userName ?? '',
            hostAvatar: b.user?.profileImage?.urlSmall
              ?? b.user?.profileImage?.urlMedium
              ?? this.avatarFallback(b.user?.displayName),
            title: b.title ?? '',
            author: b.user?.userName ?? '',
            authorDisplayName: b.user?.displayName ?? b.user?.userName ?? '',
            avatar: b.user?.profileImage?.urlSmall
              ?? b.user?.profileImage?.urlMedium
              ?? this.avatarFallback(b.user?.displayName),
            timeAgo: this.timeAgo(b.createdAt ?? ''),
            text: rawText.length > 160 ? rawText.slice(0, 160).trimEnd() + '…' : rawText,
            hasMore: rawText.length > 160,
            images,
            mediaBlocks,
            activeSlide: 0,
            likes: totalReactions,
            comments: b.numberOfComments ?? 0,
            shares: b.numberOfShares ?? 0,
            myReaction,
            reactions: totalReactions,
            totalReactions,
            reactionCounts: { ...reactionCounts },
            recentUserName: b?.reactions?.recentUserName ?? null,
            topTypes: (b?.reactions?.topTypes ?? []) as ReactionType[],
            showEmojiPicker: false,
            hoveredReaction: null,
            isFollowed: b.isFollowed ?? false,
            isMyBlog: b.isMyBlog ?? false,
            friendshipStatus: (b?.friendship?.status ?? 'NONE') as FriendshipStatus,
            friendshipRequestId: b?.friendship?.requestId ?? null,
            acceptedCollaborators: recCollaborators,
          };
        });
        this.recsLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.recsLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  // ── Recommendation card helpers ───────────────────────────────────────────

  toggleComments(): void {
    if (!this.blogDetails?.[0] || this.togglingComments) return;
    const blog = this.blogDetails[0];
    const blogId = blog.id;
    this.togglingComments = true;
    this.blogService.toggleComments(blogId).subscribe({
      next: (res: any) => {
        blog.commentsDisabled = res.data?.blogInfo?.commentsDisabled ?? !blog.commentsDisabled;
        this.togglingComments = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.togglingComments = false;
        this.cdr.markForCheck();
      }
    });
  }

  navigateToRecommendation(blogId: string): void {
    this.router.navigate(['/blog', blogId]);
  }

  goToSlide(item: RecommendationVM, index: number): void {
    item.activeSlide = index;
    this.cdr.markForCheck();
  }

  prevSlide(item: RecommendationVM, e: Event): void {
    e.stopPropagation();
    this.onSlideSwipe(item, -1);
  }

  nextSlide(item: RecommendationVM, e: Event): void {
    e.stopPropagation();
    this.onSlideSwipe(item, 1);
  }

  /** Arrow tap and touch swipe share this. `dir` is +1 for next, -1 for previous. */
  onSlideSwipe(item: RecommendationVM, dir: 1 | -1): void {
    const total = item.images.length;
    if (total <= 1) return;
    item.activeSlide = (item.activeSlide + dir + total) % total;
    this.cdr.markForCheck();
  }

  isVideoSlide(item: { mediaBlocks?: BlogBlockVM[] }, index: number): boolean {
    return item.mediaBlocks?.[index]?.type === 'VIDEO';
  }

  trackById(_: number, item: RecommendationVM): string { return item._id ?? String(_); }
  trackByIndex(index: number): number { return index; }

  private timeAgo(dateStr: string): string {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    const w = Math.floor(d / 7);
    if (w < 5) return `${w}w ago`;
    return `${Math.floor(d / 30)}mo ago`;
  }

  private mapBlogs(apiBlogs: any[]): BlogDetailsVM[] {
    return apiBlogs.map((b: any) => {
      const raw = b?.reactions?.myReaction;
      const rawType: string | null =
        raw && typeof raw === 'object' ? (raw.type ?? null) : (raw ?? null);
      const myReaction: ReactionType | null =
        rawType && REACTIONS.some(r => r.type === rawType)
          ? (rawType as ReactionType)
          : null;

      const blocks = (b?.blogInfo?.blocks ?? [])
        .slice()
        .sort((a: any, c: any) => a.position - c.position)
        .map((blk: any): BlogBlockVM => {
          if (blk.type === 'TEXT') {
            return { type: 'TEXT', html: blk.text ?? '' };
          }
          const url = blk.mediaId?.urlLarge
            ?? blk.mediaId?.urlMedium
            ?? blk.mediaId?.urlSmall
            ?? blk.mediaId?.url
            ?? '';
          return { type: blk.type as 'IMAGE' | 'VIDEO', url };
        });

      return {
        id: b._id,
        hostId: b?.user?._id ?? '',
        userId: b?.user?._id ?? '',
        hostName: b?.user?.userName ?? 'Unknown',
        hostAvatar: b?.user?.profileImage?.urlSmall
          ?? this.avatarFallback(b?.user?.displayName ?? b?.user?.userName),
        timeAgo: this.getTimeAgo(b?.createdAt),
        title: b?.title ?? '',
        blocks,
        hashtags: b?.hashtags ?? [],
        likes: b?.reactions?.total ?? b?.numberOfLikes ?? 0,
        comments: b?.numberOfComments ?? 0,
        shares: b?.numberOfShares ?? 0,
        myReaction,
        reactionCounts: b?.reactions?.counts ?? { LIKE: 0, HEART: 0, HUG: 0, WOW: 0, SAD: 0, HAHA: 0 },
        totalReactions: b?.reactions?.total ?? 0,
        recentUserName: b?.reactions?.recentUserName ?? null,
        topTypes: (b?.reactions?.topTypes ?? []) as ReactionType[],
        showEmojiPicker: false,
        hoveredReaction: null,
        friendshipStatus: (b?.friendship?.status ?? 'NONE') as FriendshipStatus,
        friendshipRequestId: b?.friendship?.requestId ?? null,
        isFollowed: b?.isFollowed ?? false,
        isMyBlog: b?.isMyBlog ?? false,
        acceptedCollaborators: (b?.blogInfo?.collaborations ?? [])
          .filter((c: any) => c.status === 'ACCEPTED')
          .map((c: any) => ({
            _id: c.user?._id ?? '',
            displayName: c.user?.displayName ?? '',
            userName: c.user?.userName ?? '',
            profileImage: c.user?.profileImage ?? undefined,
            friendship: c.friendship ?? c.user?.friendship ?? { status: 'NONE', requestId: null },
            isFollowed: c.isFollowed ?? c.user?.isFollowed ?? false,
            isMe: c.isMe ?? false,
          })),
        mentions: (b?.blogInfo?.mentions ?? []).map((u: any) => ({
          _id: u._id ?? '',
          displayName: u.displayName ?? '',
          userName: u.userName ?? '',
          profileImage: u.profileImage ?? undefined,
        })),
        commentsDisabled: b?.blogInfo?.commentsDisabled ?? false,
        rawCollaborations: b?.blogInfo?.collaborations ?? [],
        isFavourite: (b as any)?.isFavourite ?? false,
        isRemovedByAdmin: (b as any)?.isRemovedByAdmin ?? false,
      };
    });
  }

  // ── Reactions ─────────────────────────────────────────────────────────────

  getReactionConfig(type: ReactionType | null): typeof REACTIONS[0] | null {
    if (!type) return null;
    return REACTIONS.find(r => r.type === type) ?? null;
  }

  onReactionClick(card: ReactionCardVM): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getBlogDisplayName(card as any), 'react to this blog by');
      return;
    }
    if (card.showEmojiPicker) {
      card.showEmojiPicker = false;
    } else {
      // this.soundService.playPickerOpen();
      card.showEmojiPicker = true;
    }
    this.cdr.markForCheck();
  }

  onEmojiPick(card: ReactionCardVM, type: ReactionType, $event: MouseEvent): void {
    $event.stopPropagation();
    card.showEmojiPicker = false;
    card.hoveredReaction = null;
    // this.soundService.playReactionSelect(type);
    this.submitReaction(card, type);
  }

  private submitReaction(card: ReactionCardVM, type: ReactionType): void {
    const cardId = card.id ?? card._id;
    if (!cardId) return;
    const previous = card.myReaction;
    const previousTopTypes = [...(card.topTypes ?? [])];
    const previousRecentUserName = card.recentUserName;

    if (previous === type) {
      card.reactionCounts[type] = Math.max(0, card.reactionCounts[type] - 1);
      card.totalReactions = Math.max(0, card.totalReactions - 1);
      card.myReaction = null;
    } else {
      if (previous) {
        card.reactionCounts[previous] = Math.max(0, card.reactionCounts[previous] - 1);
        card.totalReactions = Math.max(0, card.totalReactions - 1);
      }
      card.reactionCounts[type]++;
      card.totalReactions++;
      card.myReaction = type;
    }
    card.likes = card.totalReactions;
    if (typeof card.reactions === 'number') {
      card.reactions = card.totalReactions;
    }

    // Update topTypes and recentUserName immediately
    card.topTypes = this.computeTopTypes(card.reactionCounts);
    const currentUserName = this.authState.getAuthSnapshot()?.user?.userName ?? null;
    if (card.myReaction) {
      card.recentUserName = currentUserName;
    } else if (card.totalReactions === 0) {
      card.recentUserName = null;
    }

    this.cdr.markForCheck();

    this.blogService.reactToBlog(cardId, type).subscribe({
      error: () => {
        card.myReaction = previous;
        card.reactionCounts = { ...card.reactionCounts };
        card.totalReactions = Object.values(card.reactionCounts).reduce((a, b) => a + b, 0);
        card.likes = card.totalReactions;
        if (typeof card.reactions === 'number') {
          card.reactions = card.totalReactions;
        }
        card.topTypes = previousTopTypes;
        card.recentUserName = previousRecentUserName;
        this.cdr.markForCheck();
      },
    });
  }

  private computeTopTypes(counts: ReactionCounts): ReactionType[] {
    return (Object.entries(counts) as [ReactionType, number][])
      .filter(([, count]) => count > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([type]) => type);
  }

  closeEmojiPicker(card: ReactionCardVM): void {
    card.showEmojiPicker = false;
    card.hoveredReaction = null;
    this.cdr.markForCheck();
  }

  toggleLike(card: RecommendationVM, event: MouseEvent): void {
    event.stopPropagation();
    this.blogService.reactToBlog(card._id, 'LIKE').subscribe({
      next: (res: any) => {
        const data = res?.data;
        if (!data) return;
        card.myReaction = data.reactions?.myReaction ?? null;
        const total = data.reactions?.total ?? data.numberOfLikes ?? card.totalReactions ?? card.likes;
        card.totalReactions = total;
        card.reactions = total;
        card.likes = total;
        if (data.reactions?.counts) {
          card.reactionCounts = { ...data.reactions.counts };
        }
        this.cdr.markForCheck();
      },
    });
  }

  // ── Comments ──────────────────────────────────────────────────────────────

  loadComments(page = 1): void {
    if (!this.blogId) return;
    this.commentsLoading = true;
    this.cdr.markForCheck();

    this.commentService.getComments(this.blogId, page, 'BLOG').subscribe({
      next: (res: any) => {
        if (page === 1) {
          this.comments = res.data.items ?? [];
        } else {
          this.comments = [...this.comments, ...(res.data.items ?? [])];
        }
        this.commentsTotal = res.data.meta.total ?? 0;
        this.commentsPage = res.data.meta.page ?? 1;
        this.commentsTotalPages = res.data.meta.totalPages ?? 1;
        this.commentsLoading = false;
        this.commentsLoaded = true;
        this.cdr.markForCheck();
      },
      error: () => {
        this.commentsLoading = false;
        this.commentsLoaded = true;
        this.cdr.markForCheck();
      },
    });
  }

  loadMoreComments(): void {
    if (this.commentsPage < this.commentsTotalPages) {
      this.loadComments(this.commentsPage + 1);
    }
  }

  submitComment(): void {
    if (!this.isLoggedIn()) {
      const blog = this.blogDetails?.[0];
      this.openLoginPrompt(this.getBlogDisplayName(blog ?? {}), 'comment on this blog by');
      return;
    }
    if (!this.blogId) return;
    const body = this.commentInput.trim();
    if (!body && !this.selectedGifUrl) return;

    const finalBody = this.selectedGifUrl
      ? (body ? `${body}\n${this.selectedGifUrl}` : this.selectedGifUrl)
      : body;

    this.submitting = true;
    this.cdr.markForCheck();

    this.commentService.createComment({
      targetType: 'BLOG',
      targetId: this.blogId,
      body: finalBody,
    }).subscribe({
      next: (res: any) => {
        const newComment: IComment = {
          ...res.data,
          replies: [],
          ableToReply: true,
          ableToModification: true,
        };
        this.comments = [newComment, ...this.comments];
        this.commentsTotal++;
        this.commentInput = '';
        this.selectedGifUrl = null;
        if (this.mainDirRef) this.mainDirRef.setText('');
        this.submitting = false;
        if (this.blogDetails?.[0]) this.blogDetails[0].comments = (this.blogDetails[0].comments ?? 0) + 1;
        this.closeMentionDropdown();
        this.cdr.markForCheck();
      },
      error: () => { this.submitting = false; this.cdr.markForCheck(); },
    });
  }

  submitReply(parentId: string): void {
    if (!this.blogId) return;
    const body = (this.replyInputs[parentId] ?? '').trim();
    const gifUrl = this.replyGifUrls[parentId] ?? null;
    if ((!body && !gifUrl) || this.submitting) return;

    const finalBody = gifUrl
      ? (body ? `${body}\n${gifUrl}` : gifUrl)
      : body;

    this.submitting = true;
    this.cdr.markForCheck();

    this.commentService.createComment({
      targetType: 'BLOG',
      targetId: this.blogId,
      parentId,
      body: finalBody,
    }).subscribe({
      next: (res: any) => {
        const reply: IReply = { ...res.data, ableToModification: true };
        const parent = this.comments.find(c => c._id === parentId);
        if (parent) {
          parent.replies = [reply, ...(parent.replies ?? [])];
          parent.replyCount = (parent.replyCount ?? 0) + 1;
        }
        if (this.blogDetails?.[0]) this.blogDetails[0].comments = (this.blogDetails[0].comments ?? 0) + 1;
        this.replyInputs[parentId] = '';
        delete this.replyGifUrls[parentId];
        this.replyingTo = null;
        if (this.replyDirRef) this.replyDirRef.setText('');
        this.showGifPicker = false;
        this.submitting = false;
        this.closeMentionDropdown();
        this.cdr.markForCheck();
      },
      error: () => { this.submitting = false; this.cdr.markForCheck(); },
    });
  }

  toggleReply(commentId: string): void {
    this.replyingTo = this.replyingTo === commentId ? null : commentId;
    if (this.replyingTo && !this.replyInputs[commentId]) {
      this.replyInputs[commentId] = '';
    }
    this.cdr.markForCheck();
    if (this.replyingTo) {
      setTimeout(() => {
        document.getElementById('reply-input-' + commentId)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }

  toggleDropdown(id: string): void {
    this.openDropdownId = this.openDropdownId === id ? null : id;
    this.cdr.markForCheck();
  }

  closeDropdown(): void {
    this.openDropdownId = null;
    this.cdr.markForCheck();
  }

  startEdit(item: IComment | IReply): void {
    this.editingCommentId = item._id;
    this.editInput = item.body;
    this.cdr.markForCheck();
  }

  cancelEdit(): void {
    this.editingCommentId = null;
    this.editInput = '';
    this.cdr.markForCheck();
  }

  saveEdit(item: IComment | IReply): void {
    const body = this.editInput.trim();
    if (!body || this.submitting) return;
    this.submitting = true;
    this.cdr.markForCheck();

    this.commentService.updateComment(item._id, body).subscribe({
      next: (res: any) => {
        item.body = res.data.body ?? body;
        item.isEdited = true;
        this.editingCommentId = null;
        this.editInput = '';
        this.submitting = false;
        this.cdr.markForCheck();
      },
      error: () => { this.submitting = false; this.cdr.markForCheck(); },
    });
  }

  deleteComment(commentId: string, parentId?: string): void {
    this.commentService.deleteComment(commentId).subscribe({
      next: (res: any) => {
        if (parentId) {
          const parent = this.comments.find(c => c._id === parentId);
          if (parent) {
            parent.replies = parent.replies.filter(r => r._id !== commentId);
            parent.replyCount = Math.max(0, (parent.replyCount ?? 1) - 1);
          }
        } else {
          this.comments = this.comments.filter(c => c._id !== commentId);
          this.commentsTotal = Math.max(0, this.commentsTotal - 1);
        }
        // The API returns the real count (deleting a comment also removes its
        // replies), so use that rather than guessing locally.
        if (this.blogDetails?.[0]) {
          this.blogDetails[0].comments = res?.data?.numberOfComments
            ?? Math.max(0, (this.blogDetails[0].comments ?? 0) - 1);
        }
        this.cdr.markForCheck();
      },
      error: () => { this.cdr.markForCheck(); },
    });
  }

  // ── Report comment modal ────────────────────────────────────────────────
  isCommentReportModalOpen = false;
  isSubmittingCommentReport = false;
  commentReportError = '';
  commentReportSuccessOpen = false;
  private reportingCommentId: string | null = null;

  canReportComment(comment: { ableToModification: boolean }): boolean {
    return this.isLoggedIn() && !comment.ableToModification;
  }

  reportComment(commentId: string): void {
    this.closeDropdown();
    this.reportingCommentId = commentId;
    this.commentReportError = '';
    this.isCommentReportModalOpen = true;
    this.cdr.markForCheck();
  }

  closeCommentReportModal(): void {
    if (this.isSubmittingCommentReport) return;
    this.isCommentReportModalOpen = false;
    this.commentReportError = '';
    this.cdr.markForCheck();
  }

  submitCommentReport(description: string): void {
    if (!this.reportingCommentId) return;
    this.isSubmittingCommentReport = true;
    this.commentReportError = '';

    this.reportService.submitCommentReport(this.reportingCommentId, description).subscribe({
      next: () => {
        this.isSubmittingCommentReport = false;
        this.isCommentReportModalOpen = false;
        this.commentReportSuccessOpen = true;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isSubmittingCommentReport = false;
        this.commentReportError = err?.error?.message ?? 'Failed to submit report. Please try again.';
        this.cdr.markForCheck();
      },
    });
  }

  closeCommentReportSuccess(): void {
    this.commentReportSuccessOpen = false;
    this.cdr.markForCheck();
  }

  // ── Comment body helpers ──────────────────────────────────────────────────

  /**
   * Returns the text/HTML portion of a comment body (everything except GIF lines).
   * Rich HTML (tables, blockquotes, code, etc.) is preserved as-is.
   */
  getTextPart(body: string): string {
    if (!body) return '';
    // Remove bare GIF URLs that were appended on a separate line
    return body
      .split('\n')
      .filter(line => !this.isGifUrl(line.trim()))
      .join('\n')
      .trim();
  }

  getGifPart(body: string): string | null {
    if (!body) return null;
    return body.split('\n').find(line => this.isGifUrl(line.trim())) ?? null;
  }

  private isGifUrl(str: string): boolean {
    return /^https?:\/\/.*(giphy\.com|\.gif(\?.*)?$)/.test(str);
  }

  /** Single source of truth for the ui-avatars fallback used across this page. */
  avatarFallback(name?: string | null): string {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFD4E0&color=D31148`;
  }

  getUserAvatar(item: IComment | IReply): string {
    const user = (item as any).user;
    return user?.profileImage?.urlSmall
      ?? this.avatarFallback(user?.displayName);
  }

  // ── Emoji helpers ─────────────────────────────────────────────────────────

  appendQuickEmoji(emoji: string): void {
    this.commentInput += emoji;
    if (this.mainDirRef) this.mainDirRef.setText(this.commentInput);
    this.cdr.markForCheck();
  }

  toggleEmojiPicker(event: MouseEvent, context: string | null): void {
    event.stopPropagation();
    this.activeEmojiContext = context;
    this.showEmojiPicker = !this.showEmojiPicker;
    this.showGifPicker = false;
    this.cdr.markForCheck();
  }

  pickEmoji(emoji: string): void {
    const ctx = this.activeEmojiContext;
    if (!ctx || ctx === 'main') {
      this.commentInput += emoji;
      if (this.mainDirRef) this.mainDirRef.setText(this.commentInput);
    } else {
      this.replyInputs[ctx] = (this.replyInputs[ctx] ?? '') + emoji;
      if (this.replyDirRef) this.replyDirRef.setText(this.replyInputs[ctx]);
    }
    this.showEmojiPicker = false;
    this.cdr.markForCheck();
  }

  setEmojiTab(i: number): void {
    this.activeEmojiTab = i;
    this.cdr.markForCheck();
  }

  // ── GIF helpers ───────────────────────────────────────────────────────────

  toggleGifPicker(context: string | null, event: MouseEvent): void {
    event.stopPropagation();
    this.activeEmojiContext = context;
    this.showGifPicker = !this.showGifPicker;
    this.showEmojiPicker = false;
    if (this.showGifPicker && this.gifResults.length === 0) {
      this.searchGifs('trending');
    }
    this.cdr.markForCheck();
  }

  searchGifs(query?: string): void {
    const q = query ?? this.gifSearchQuery.trim();
    if (!q) return;
    this.gifLoading = true;
    this.cdr.markForCheck();

    const url = q === 'trending'
      ? `https://api.giphy.com/v1/gifs/trending?api_key=${this.GIPHY_KEY}&limit=20&rating=g`
      : `https://api.giphy.com/v1/gifs/search?api_key=${this.GIPHY_KEY}&q=${encodeURIComponent(q)}&limit=20&rating=g`;

    fetch(url)
      .then(r => r.json())
      .then((data: any) => {
        this.gifResults = (data.data ?? []).map((g: any) => ({
          id: g.id,
          url: g.images.original.url,
          preview: g.images.fixed_height_small?.url ?? g.images.fixed_width_small?.url,
        }));
        this.gifLoading = false;
        this.cdr.markForCheck();
      })
      .catch(() => { this.gifLoading = false; this.cdr.markForCheck(); });
  }

  selectGif(gif: { url: string; preview: string }): void {
    if (this.activeEmojiContext && this.activeEmojiContext !== 'main') {
      this.replyGifUrls[this.activeEmojiContext] = gif.url;
    } else {
      this.selectedGifUrl = gif.url;
    }
    this.showGifPicker = false;
    this.cdr.markForCheck();
  }

  removeGif(context: 'main' | string = 'main'): void {
    if (context !== 'main') {
      delete this.replyGifUrls[context];
    } else {
      this.selectedGifUrl = null;
    }
    this.cdr.markForCheck();
  }

  onGifSearchKey(e: KeyboardEvent): void {
    if (e.key === 'Enter') this.searchGifs();
  }

  // ── Follow / Friend ───────────────────────────────────────────────────────

  friendButtonTitle(status: FriendshipStatus): string {
    const map: Record<FriendshipStatus, string> = {
      NONE: 'Send friend request',
      PENDING_SENT: 'Cancel friend request',
      PENDING_RECEIVED: 'Accept friend request',
      FRIENDS: 'Unfriend',
      BLOCKED: 'Blocked',
    };
    return map[status];
  }

  onFriendButtonClick(card: SocialCardVM, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getBlogDisplayName(card as any), 'add as a friend');
      return;
    }
    switch (card.friendshipStatus) {
      case 'NONE': this.sendFriendRequest(card, event); break;
      case 'PENDING_SENT': this.cancelFriendRequest(card, event); break;
      case 'PENDING_RECEIVED': this.acceptFriendRequest(card, event); break;
      case 'FRIENDS': this.promptUnfriend(card, event); break;
      case 'BLOCKED': break;
    }
  }

  showUnfriendConfirm = false;
  private pendingUnfriendAction: (() => void) | null = null;

  private promptUnfriend(card: SocialCardVM, event: MouseEvent): void {
    event.stopPropagation();
    this.pendingUnfriendAction = () => this.unfriend(card, event);
    this.showUnfriendConfirm = true;
  }

  confirmUnfriend(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction?.();
    this.pendingUnfriendAction = null;
  }

  cancelUnfriendConfirm(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction = null;
  }

  private sendFriendRequest(card: SocialCardVM, event: MouseEvent): void {
    event.stopPropagation();
    if (card.friendshipStatus !== 'NONE') return;
    card.friendshipStatus = 'PENDING_SENT';
    this.cdr.markForCheck();
    this.friendRequestService.send(card.userId).subscribe({
      next: (res: any) => {
        card.friendshipRequestId = res?.data?._id ?? null;
        this.cdr.markForCheck();
      },
      error: (err) => {
        const status = err?.error?.statusCode ?? err?.status;
        if (status !== 409) card.friendshipStatus = 'NONE';
        const cooldownHours = this.friendRequestService.parseCooldownError(err);
        if (cooldownHours !== null) {
          this.friendLimitHours = cooldownHours;
          this.showFriendLimitPopup = true;
        }
        this.cdr.markForCheck();
      },
    });
  }

  private acceptFriendRequest(card: SocialCardVM, event: MouseEvent): void {
    event.stopPropagation();
    if (!card.friendshipRequestId) return;
    const prevStatus = card.friendshipStatus;
    card.friendshipStatus = 'FRIENDS';
    this.cdr.markForCheck();
    this.friendRequestService.accept(card.friendshipRequestId).subscribe({
      error: () => {
        card.friendshipStatus = prevStatus;
        this.cdr.markForCheck();
      },
    });
  }

  private cancelFriendRequest(card: SocialCardVM, event: MouseEvent): void {
    event.stopPropagation();
    if (!card.friendshipRequestId) return;
    const prevStatus = card.friendshipStatus;
    const prevRequestId = card.friendshipRequestId;
    card.friendshipStatus = 'NONE';
    this.cdr.markForCheck();
    this.friendRequestService.cancel(card.friendshipRequestId).subscribe({
      error: () => {
        card.friendshipStatus = prevStatus;
        card.friendshipRequestId = prevRequestId;
        this.cdr.markForCheck();
      },
    });
  }

  private unfriend(card: SocialCardVM, event: MouseEvent): void {
    event.stopPropagation();
    const prevStatus = card.friendshipStatus;
    const prevRequestId = card.friendshipRequestId;
    card.friendshipStatus = 'NONE';
    card.friendshipRequestId = null;
    this.cdr.markForCheck();
    this.friendRequestService.unfriend(card.userId).subscribe({
      error: () => {
        card.friendshipStatus = prevStatus;
        card.friendshipRequestId = prevRequestId;
        this.cdr.markForCheck();
      },
    });
  }

  onBlogFollowClick(event?: MouseEvent): void {
    event?.stopPropagation();
    if (!this.blogDetails?.[0]) return;
    if (!this.blogDetails?.[0]) return;
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getBlogDisplayName(this.blogDetails[0]), 'follow');
      return;
    }
    const blog = this.blogDetails[0];

    if (blog.isFollowed) {
      blog.isFollowed = false;
      this.cdr.markForCheck();
      this.relationshipService.unfollow(blog.hostId).subscribe({
        error: () => { blog.isFollowed = true; this.cdr.markForCheck(); },
      });
    } else {
      blog.isFollowed = true;
      this.cdr.markForCheck();
      this.relationshipService.follow(blog.hostId).subscribe({
        error: () => { blog.isFollowed = false; this.cdr.markForCheck(); },
      });
    }
  }

  onRecommendationFollowClick(card: RecommendationVM, event: MouseEvent): void {
    event.stopPropagation();
    if (!card.userId) return;
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(this.getBlogDisplayName(card), 'follow');
      return;
    }
    if (card.isFollowed) {
      card.isFollowed = false;
      this.cdr.markForCheck();
      this.relationshipService.unfollow(card.userId).subscribe({
        error: () => { card.isFollowed = true; this.cdr.markForCheck(); },
      });
    } else {
      card.isFollowed = true;
      this.cdr.markForCheck();
      this.relationshipService.follow(card.userId).subscribe({
        error: () => { card.isFollowed = false; this.cdr.markForCheck(); },
      });
    }
  }

  getCurrentUserAvatar(): string {
    const user = this.authState.getAuthSnapshot()?.user;

    return user?.profileImage?.urlSmall
      ?? user?.profileImage?.urlMedium
      ?? this.avatarFallback(user?.displayName || user?.userName);
  }

  openReactionModal(card: RecommendationVM, event: MouseEvent): void {
    event.stopPropagation();
    this.isReactionOpen = true;
    this.activeReactionFilter = null;
    this.activeReactionBlogId = card._id;
    this.loadReactions(card._id);
  }

  closeReactionModal(): void {
    this.isReactionOpen = false;
    this.reactionData = null;
    this.activeReactionBlogId = null;
    this.cdr.markForCheck();
  }

  setReactionFilter(type: ReactionType | null): void {
    this.activeReactionFilter = type;
    if (this.activeReactionBlogId) {
      this.loadReactions(this.activeReactionBlogId, type ?? undefined);
    }
  }

  private loadReactions(blogId: string, type?: ReactionType): void {
    this.reactionsLoading = true;
    this.cdr.markForCheck();
    this.blogService.getReactions(blogId, type).subscribe({
      next: (res: any) => {
        this.reactionData = res?.data;
        this.reactionsLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.reactionsLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  showCollaboratorsModal(blog: any): void {
    this.selectedBlog = blog;
    this.showCollabModal = true;
    this.cdr.markForCheck();
  }

  closeCollaboratorsModal(): void {
    this.showCollabModal = false;
    this.selectedBlog = null;
    this.cdr.markForCheck();
  }

  showTaggedPeopleModal(blog: any, event?: Event): void {
    event?.stopPropagation();
    this.selectedBlog = blog;
    this.showTaggedModal = true;
    this.cdr.markForCheck();
  }

  closeTaggedPeopleModal(): void {
    this.showTaggedModal = false;
    this.selectedBlog = null;
    this.cdr.markForCheck();
  }

  onCollabFriendClick(collab: BlogCollaborator, event: MouseEvent): void {
    event.stopPropagation();
    const status = collab.friendship.status ?? 'NONE';
    if (status === 'NONE') {
      collab.friendship.status = 'PENDING_SENT';
      this.cdr.markForCheck();
      this.friendRequestService.send(collab._id).subscribe({
        next: (res: any) => { collab.friendship.requestId = res?.data?._id ?? null; this.cdr.markForCheck(); },
        error: (err: any) => {
          const code = err?.error?.statusCode ?? err?.status;
          if (code !== 409) collab.friendship.status = 'NONE';
          const cooldownHours = this.friendRequestService.parseCooldownError(err);
          if (cooldownHours !== null) {
            this.friendLimitHours = cooldownHours;
            this.showFriendLimitPopup = true;
          }
          this.cdr.markForCheck();
        },
      });
    } else if (status === 'PENDING_SENT') {
      const prev = collab.friendship.status;
      const prevId = collab.friendship.requestId;
      collab.friendship.status = 'NONE';
      this.cdr.markForCheck();
      this.friendRequestService.cancel(collab.friendship.requestId!).subscribe({
        error: () => { collab.friendship.status = prev; collab.friendship.requestId = prevId; this.cdr.markForCheck(); },
      });
    } else if (status === 'FRIENDS') {
      this.pendingUnfriendAction = () => {
        const prev = collab.friendship.status;
        collab.friendship.status = 'NONE';
        collab.friendship.requestId = null;
        this.cdr.markForCheck();
        this.friendRequestService.unfriend(collab._id).subscribe({
          error: () => { collab.friendship.status = prev; this.cdr.markForCheck(); },
        });
      };
      this.showUnfriendConfirm = true;
    }
  }

  onCollabFollowClick(collab: BlogCollaborator, event: MouseEvent): void {
    event.stopPropagation();
    if (collab.isFollowed) {
      collab.isFollowed = false;
      this.cdr.markForCheck();
      this.relationshipService.unfollow(collab._id).subscribe({
        error: () => { collab.isFollowed = true; this.cdr.markForCheck(); },
      });
    } else {
      collab.isFollowed = true;
      this.cdr.markForCheck();
      this.relationshipService.follow(collab._id).subscribe({
        error: () => { collab.isFollowed = false; this.cdr.markForCheck(); },
      });
    }
  }

  get filteredReactions(): any[] { return this.reactionData?.all ?? []; }

  onModalFollowClick(item: any, event: MouseEvent): void {
    event.stopPropagation();
    if (!item.user?._id) return;
    if (item.isFollowed) {
      item.isFollowed = false;
      this.cdr.markForCheck();
      this.relationshipService.unfollow(item.user._id).subscribe({
        error: () => { item.isFollowed = true; this.cdr.markForCheck(); },
      });
    } else {
      item.isFollowed = true;
      this.cdr.markForCheck();
      this.relationshipService.follow(item.user._id).subscribe({
        error: () => { item.isFollowed = false; this.cdr.markForCheck(); },
      });
    }
  }

  onModalFriendClick(item: any, event: MouseEvent): void {
    event.stopPropagation();
    const status = item.friendship?.status ?? 'NONE';
    const requestId = item.friendship?.requestId ?? null;
    const userId = item.user?._id;

    if (!userId) return;

    if (status === 'NONE') {
      item.friendship.status = 'PENDING_SENT';
      this.cdr.markForCheck();
      this.friendRequestService.send(userId).subscribe({
        next: (res: any) => {
          item.friendship.requestId = res?.data?._id ?? null;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          const code = err?.error?.statusCode ?? err?.status;
          if (code !== 409) item.friendship.status = 'NONE';
          const cooldownHours = this.friendRequestService.parseCooldownError(err);
          if (cooldownHours !== null) {
            this.friendLimitHours = cooldownHours;
            this.showFriendLimitPopup = true;
          }
          this.cdr.markForCheck();
        },
      });
    } else if (status === 'PENDING_SENT' && requestId) {
      item.friendship.status = 'NONE';
      this.cdr.markForCheck();
      this.friendRequestService.cancel(requestId).subscribe({
        error: () => {
          item.friendship.status = 'PENDING_SENT';
          item.friendship.requestId = requestId;
          this.cdr.markForCheck();
        },
      });
    } else if (status === 'PENDING_RECEIVED' && requestId) {
      item.friendship.status = 'FRIENDS';
      this.cdr.markForCheck();
      this.friendRequestService.accept(requestId).subscribe({
        error: () => {
          item.friendship.status = 'PENDING_RECEIVED';
          this.cdr.markForCheck();
        },
      });
    } else if (status === 'FRIENDS') {
      this.pendingUnfriendAction = () => {
        item.friendship.status = 'NONE';
        this.cdr.markForCheck();
        this.friendRequestService.unfriend(userId).subscribe({
          error: () => {
            item.friendship.status = 'FRIENDS';
            this.cdr.markForCheck();
          },
        });
      };
      this.showUnfriendConfirm = true;
    }
  }

  // ── Utilities ─────────────────────────────────────────────────────────────

  getTimeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m} minute${m > 1 ? 's' : ''} ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hour${h > 1 ? 's' : ''} ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d} day${d > 1 ? 's' : ''} ago`;
    const w = Math.floor(d / 7);
    if (w < 5) return `${w} week${w > 1 ? 's' : ''} ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo} month${mo > 1 ? 's' : ''} ago`;
    return `${Math.floor(mo / 12)} year${Math.floor(mo / 12) > 1 ? 's' : ''} ago`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    if (!this.elRef.nativeElement.querySelector('.comment-picker-area')?.contains(target)) {
      this.showEmojiPicker = false;
      this.showGifPicker = false;
      this.closeMentionDropdown();
      this.cdr.markForCheck();
    }
  }

  private isLoggedIn(): boolean {
    return this.authState.getAuthSnapshot()?.isLoggedIn === true;
  }

  private openLoginPrompt(userName: string, action: string): void {
    this.loginPromptUserName = userName;
    this.loginPromptAction = action;
    this.showLoginPrompt = true;
    this.cdr.markForCheck();
  }

  closeLoginPrompt(): void {
    this.showLoginPrompt = false;
    this.cdr.markForCheck();
  }

  goToLogin(): void {
    this.closeLoginPrompt();
    this.router.navigate(['/login']);
  }

  // ── Share ────────────────────────────────────────────────────────────────────

  openShareSheet(): void {
    this.shareCount = this.blogDetails?.[0]?.shares ?? 0;
    this.shareConfig = {
      contentId: this.blogDetails?.[0]?.id ?? '',
      contentType: 'blog',
      title: this.blogDetails?.[0]?.title ?? 'Check out this blog',
      description: this.blogDetails?.[0]?.title,
      imageUrl: undefined,
    };
    this.cdr.markForCheck();
  }

  closeShareSheet(): void {
    this.shareConfig = null;
    this.cdr.markForCheck();
  }

  onShareCountUpdated(count: number): void {
    this.shareCount = count; // this.blogDetails?.[0]?.shares ?? 0;
    this.shareCount = count;
    this.cdr.markForCheck();
  }

  // ── Favourites ─────────────────────────────────────────────────────────────

  toggleFavourite(): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt('this blog', 'save to favourites');
      return;
    }
    const blog = this.blogDetails?.[0];
    if (!blog) return;
    const prev = this.isFavourite;
    this.isFavourite = !prev;
    this.cdr.markForCheck();
    this.favouriteService.toggle(blog.id).subscribe({
      next: (res: any) => {
        this.isFavourite = res?.data?.isFavourite ?? !prev;
        this.favouriteService.broadcastToggle(blog.id, this.isFavourite);
        this.cdr.markForCheck();
      },
      error: () => { this.isFavourite = prev; this.cdr.markForCheck(); },
    });
  }

  private getBlogDisplayName(card: { hostName?: string; author?: string; acceptedCollaborators?: BlogCollaborator[] }): string {
    const name = card.hostName ?? card.author ?? '';
    const collabNames = (card.acceptedCollaborators ?? [])
      .map(c => c.userName)
      .join(', ');
    return collabNames ? `${name} & ${collabNames}` : name;
  }

  // ── Edit / Delete / Leave Collaboration ──────────────────────────────────

  showActionMenu = false;
  showDeleteConfirm = false;
  showLeaveConfirm = false;
  isDeleting = false;
  isLeaving = false;

  // ── Report blog modal ────────────────────────────────────────────────────
  isReportModalOpen = false;
  isSubmittingReport = false;
  reportError = '';
  reportSuccessOpen = false;
  private reportService = inject(ReportService);

  // ── Block user (safeword-confirm) modal ─────────────────────────────────
  showBlockUserConfirm = false;

  /** Bound to <app-block-user-confirm>'s [targetName] — "You're about to
   *  block {name}". */
  get blogAuthorDisplayName(): string {
    return this.blogDetails?.[0]?.hostName ?? '';
  }

  get isCollaborator(): boolean {
    const authUser = this.authState.getAuthSnapshot()?.user;
    const userId = authUser?._id ?? authUser?.id;
    if (!userId || !this.blogDetails?.[0]) return false;
    const blog = this.blogDetails[0];
    return (blog.rawCollaborations ?? []).some(
      (c: any) => {
        const collabUserId = c.user?._id ?? c.user?.id ?? c.user;
        return collabUserId === userId || collabUserId?.toString() === userId?.toString();
      },
    );
  }

  get myCollabStatus(): 'PENDING' | 'ACCEPTED' | 'DECLINED' | null {
    const authUser = this.authState.getAuthSnapshot()?.user;
    const userId = authUser?._id ?? authUser?.id;
    if (!userId || !this.blogDetails?.[0]) return null;
    const collab = (this.blogDetails[0].rawCollaborations ?? []).find((c: any) => {
      const id = c.user?._id ?? c.user?.id ?? c.user;
      return id === userId || id?.toString() === userId?.toString();
    });
    return collab?.status ?? null;
  }

  // ── Collaboration accept / decline ────────────────────────────────────────
  private collabService = inject(CollabrationRequestService);
  isCollabActioning = false;

  acceptCollaboration(): void {
    if (this.isCollabActioning || !this.blogDetails?.[0]) return;
    this.isCollabActioning = true;
    this.cdr.markForCheck();
    const blogId = this.blogDetails[0].id;
    this.collabService.blogCollabrationAccept(blogId).subscribe({
      next: () => {
        this.updateMyCollabStatus('ACCEPTED');
        this.isCollabActioning = false;
        this.blogService.triggerRefresh();
        this.cdr.markForCheck();
      },
      error: () => { this.isCollabActioning = false; this.cdr.markForCheck(); },
    });
  }

  declineCollaboration(): void {
    if (this.isCollabActioning || !this.blogDetails?.[0]) return;
    this.isCollabActioning = true;
    this.cdr.markForCheck();
    const blogId = this.blogDetails[0].id;
    this.collabService.blogCollabrationDecline(blogId).subscribe({
      next: () => {
        this.updateMyCollabStatus('DECLINED');
        this.isCollabActioning = false;
        this.blogService.triggerRefresh();
        this.cdr.markForCheck();
      },
      error: () => { this.isCollabActioning = false; this.cdr.markForCheck(); },
    });
  }

  private updateMyCollabStatus(status: 'ACCEPTED' | 'DECLINED'): void {
    const authUser = this.authState.getAuthSnapshot()?.user;
    const userId = authUser?._id ?? authUser?.id;
    const collab = (this.blogDetails?.[0]?.rawCollaborations ?? []).find((c: any) => {
      const id = c.user?._id ?? c.user?.id ?? c.user;
      return id === userId || id?.toString() === userId?.toString();
    });
    if (collab) collab.status = status;
  }

  toggleActionMenu(e: MouseEvent): void {
    e.stopPropagation();
    this.showActionMenu = !this.showActionMenu;
    this.cdr.markForCheck();
  }

  closeActionMenu(): void {
    this.showActionMenu = false;
    this.cdr.markForCheck();
  }

  editBlog(): void {
    this.closeActionMenu();
    if (this.blogId) {
      this.router.navigate(['/blog/create'], { queryParams: { edit: this.blogId } });
    }
  }

  confirmDelete(): void {
    this.closeActionMenu();
    this.showDeleteConfirm = true;
    this.cdr.markForCheck();
  }

  cancelDelete(): void {
    this.showDeleteConfirm = false;
    this.cdr.markForCheck();
  }

  /** Goes back to whatever screen the user actually came from (blog listing,
   *  notification, "All" feed, profile blog tab, etc.) — most of those entry
   *  points don't carry a returnUrl, so real browser history is the only
   *  thing that reliably knows what preceded this page. Falls back to the
   *  blogs listing only when there's no in-app history to return to (e.g.
   *  the page was opened directly via a shared URL). */
  navigateBack(): void {
    if (isPlatformBrowser(this.platformId) && window.history.length > 1) {
      this.location.back();
    } else {
      this.router.navigate(['/blogs']);
    }
  }

  doDeleteBlog(): void {
    if (this.isDeleting || !this.blogId) return;
    this.isDeleting = true;
    this.cdr.markForCheck();

    this.blogService.deleteBlog(this.blogId).subscribe({
      next: () => {
        this.isDeleting = false;
        this.showDeleteConfirm = false;
        this.blogService.triggerRefresh();
        this.navigateBack();
      },
      error: (err) => {
        this.isDeleting = false;
        this.showDeleteConfirm = false;
        console.error('Delete blog error:', err);
        this.cdr.markForCheck();
      },
    });
  }

  confirmLeave(): void {
    this.closeActionMenu();
    this.showLeaveConfirm = true;
    this.cdr.markForCheck();
  }

  cancelLeave(): void {
    this.showLeaveConfirm = false;
    this.cdr.markForCheck();
  }

  doLeaveCollaboration(): void {
    if (this.isLeaving || !this.blogId) return;
    this.isLeaving = true;
    this.cdr.markForCheck();

    this.blogService.leaveCollaboration(this.blogId).subscribe({
      next: () => {
        this.isLeaving = false;
        this.showLeaveConfirm = false;
        this.blogService.triggerRefresh();
        this.navigateBack();
      },
      error: (err) => {
        this.isLeaving = false;
        this.showLeaveConfirm = false;
        console.error('Leave blog collaboration error:', err);
        this.cdr.markForCheck();
      },
    });
  }

  // ── Block user (from the blog's 3-dot menu) ───────────────────────────────

  blockBlogUser(): void {
    this.closeActionMenu();
    this.showBlockUserConfirm = true;
    this.cdr.markForCheck();
  }

  cancelBlockBlogUser(): void {
    this.showBlockUserConfirm = false;
    this.cdr.markForCheck();
  }

  confirmBlockBlogUser(): void {
    this.showBlockUserConfirm = false;
    const hostId = this.blogDetails?.[0]?.hostId;
    this.cdr.markForCheck();
    if (!hostId) return;

    this.friendRequestService.block(hostId).subscribe({
      next: () => {
        this.friendRequestService.triggerRefresh();
        // The author's content is no longer visible to us once blocked —
        // this detail page can no longer be shown, so leave it.
        this.navigateBack();
      },
      error: (err) => {
        console.error('Block user failed:', err);
        this.cdr.markForCheck();
      },
    });
  }

  reportBlog(): void {
    this.closeActionMenu();
    this.reportError = '';
    this.isReportModalOpen = true;
    this.cdr.markForCheck();
  }
  closeReportModal(): void {
    if (this.isSubmittingReport) return;
    this.isReportModalOpen = false;
    this.reportError = '';
    this.cdr.markForCheck();
  }

  submitBlogReport(description: string): void {
    if (!this.blogId) return;
    this.isSubmittingReport = true;
    this.reportError = '';

    this.reportService.submitReport(this.blogId, description).subscribe({
      next: () => {
        this.isSubmittingReport = false;
        this.isReportModalOpen = false;
        this.reportSuccessOpen = true;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isSubmittingReport = false;
        this.reportError = err?.error?.message ?? 'Failed to submit report. Please try again.';
        this.cdr.markForCheck();
      },
    });
  }

  closeReportSuccess(): void {
    this.reportSuccessOpen = false;
    this.cdr.markForCheck();
  }
}
